import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api.js';
import { EVENTS } from '@shared/events.js';
import { formatTime, timeAgo } from '@shared/format.js';
import ComparisonChart from '../components/ComparisonChart.jsx';
import { IconWarn } from '../components/icons.jsx';

const SORTS = [
  { key: 'recent', label: '最近打卡' },
  { key: 'improvement', label: '进步幅度' },
  { key: 'name', label: '昵称' },
];

function accountStatus(s) {
  if (s.disabled) return { label: '已停用', cls: 'bg-danger/15 text-danger' };
  if (s.expires_at && Date.now() > s.expires_at) return { label: '已到期', cls: 'bg-warn/15 text-warn' };
  return { label: '正常', cls: 'bg-good/15 text-good' };
}

export default function CoachHome() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [sort, setSort] = useState('recent');
  const [loading, setLoading] = useState(true);
  const [compareEvent, setCompareEvent] = useState('3x3');
  const [compareRange, setCompareRange] = useState('30d');
  const [selected, setSelected] = useState([]);
  const [course, setCourse] = useState('');
  const [courseInput, setCourseInput] = useState('');
  const [courseMsg, setCourseMsg] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    api(`/coach/students?sort=${sort}`)
      .then((d) => {
        setStudents(d.students);
        setSelected((prev) => {
          const ids = d.students.map((s) => s.id);
          const keep = prev.filter((id) => ids.includes(id));
          return keep.length ? keep : ids.slice(0, 2);
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [sort]);

  useEffect(() => {
    api('/coach/course')
      .then((d) => { setCourse(d.course_code); setCourseInput(d.course_code); })
      .catch(() => {});
  }, []);

  const todayCount = students.filter((s) => s.today_checkin).length;
  const needNudge = students.filter((s) => !s.today_checkin).length;
  const avgProgress = useMemo(() => {
    const vals = students.map((s) => s.improvement).filter((v) => v !== null);
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  }, [students]);

  const toggleSelect = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id].slice(-4)
    );

  const copyCourse = async () => {
    try {
      await navigator.clipboard.writeText(course);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* 忽略剪贴板不可用 */ }
  };

  const saveCourse = async () => {
    const code = courseInput.trim();
    if (code.length < 4) return;
    try {
      const d = await api('/coach/course', { method: 'POST', body: { course_code: code } });
      setCourse(d.course_code);
      setCourseInput(d.course_code);
      setCourseMsg('课程码已更新，旧码立即失效');
      setTimeout(() => setCourseMsg(''), 2500);
    } catch (e) {
      setCourseMsg(e.message);
      setTimeout(() => setCourseMsg(''), 2500);
    }
  };

  const genCourse = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
    let rand = '';
    for (let i = 0; i < 6; i++) rand += chars[Math.floor(Math.random() * chars.length)];
    const code = `CUBE-${rand}`;
    setCourseInput(code);
    // 生成后直接生效
    api('/coach/course', { method: 'POST', body: { course_code: code } })
      .then((d) => { setCourse(d.course_code); setCourseMsg('已生成新课程码'); setTimeout(() => setCourseMsg(''), 2500); })
      .catch(() => {});
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-100">教练后台</h1>
          <p className="text-sm text-slate-500 mt-0.5">学生管理 · 数据对比 · 督促提醒</p>
        </div>
        <div className="flex gap-1.5">
          {SORTS.map((s) => (
            <button
              key={s.key}
              onClick={() => setSort(s.key)}
              className={`tab ${sort === s.key ? 'tab-active' : 'tab-idle'}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* 总览 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Metric label="学生总数" value={students.length} suffix="人" />
        <Metric label="今日已打卡" value={todayCount} suffix="人" accent />
        <Metric label="今日未打卡" value={needNudge} suffix="人" warn={needNudge > 0} />
        <Metric
          label="平均进步幅度"
          value={avgProgress !== null ? `${avgProgress > 0 ? '+' : ''}${avgProgress}%` : '—'}
          accent={avgProgress !== null && avgProgress > 0}
          danger={avgProgress !== null && avgProgress < 0}
        />
      </div>

      {/* 学生课程码 */}
      <div className="card p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-semibold text-slate-100">学生注册课程码</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              发给已付费的学生，他们注册账号时必须填写。可随时更换，旧码立即失效。
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-lg text-accent tracking-widest select-all">{course || '加载中…'}</span>
            <button className="btn-ghost !py-1.5 !text-xs" onClick={copyCourse}>{copied ? '已复制 ✓' : '复制'}</button>
            <button className="btn-ghost !py-1.5 !text-xs" onClick={genCourse}>随机换新</button>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <input
            className="input !w-72 !py-1.5 !text-sm font-mono tracking-widest"
            value={courseInput}
            onChange={(e) => setCourseInput(e.target.value)}
            placeholder="自定义课程码，至少 4 位"
          />
          <button className="btn-primary !py-1.5 !text-xs" onClick={saveCourse}>保存</button>
          {courseMsg && <span className="text-xs text-good">{courseMsg}</span>}
        </div>
      </div>

      {loading ? (
        <div className="text-center text-sm text-slate-500 py-16">加载中…</div>
      ) : students.length === 0 ? (
        <div className="card p-10 text-center text-sm text-slate-500">
          还没有学生注册
        </div>
      ) : (
        <>
          {/* 学生列表 */}
          <div className="card overflow-hidden">
            <div className="px-5 pt-4 pb-2 flex items-center justify-between">
              <h2 className="font-semibold text-slate-100">学生列表</h2>
              <span className="text-xs text-slate-500">点击学生查看详细数据</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-700/50">
                    <th className="py-2.5 pl-5 pr-2 font-medium">学生</th>
                    <th className="py-2.5 px-2 font-medium hidden md:table-cell">主项目</th>
                    <th className="py-2.5 px-2 font-medium text-right">总成绩</th>
                    <th className="py-2.5 px-2 font-medium text-right hidden sm:table-cell">单次PB</th>
                    <th className="py-2.5 px-2 font-medium text-right hidden lg:table-cell">当前均值</th>
                    <th className="py-2.5 px-2 font-medium text-right">进步</th>
                    <th className="py-2.5 px-2 font-medium text-center">账号</th>
                    <th className="py-2.5 px-2 font-medium text-center">打卡</th>
                    <th className="py-2.5 pr-5 pl-2 font-medium text-right">最近活跃</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/40">
                  {students.map((s) => (
                    <tr key={s.id} className="hover:bg-slate-700/20 transition-colors cursor-pointer" onClick={() => navigate(`/coach/students/${s.id}`)}>
                      <td className="py-3 pl-5 pr-2">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent to-violet flex items-center justify-center text-ink-950 font-bold text-xs">
                            {(s.nickname || '?').slice(0, 1)}
                          </div>
                          <div className="leading-tight">
                            <Link to={`/coach/students/${s.id}`} className="text-slate-100 font-medium hover:text-accent">
                              {s.nickname}
                            </Link>
                            <div className="text-[11px] text-slate-500">
                              {s.today_checkin ? <span className="text-good">今日已打卡</span> : <span className="text-warn">今日未打卡</span>}
                              {s.streak > 0 && <span className="ml-1.5 text-slate-500">连续 {s.streak} 天</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-2 hidden md:table-cell text-slate-400">
                        {EVENTS.find((e) => e.key === s.main_event)?.name || s.main_event}
                      </td>
                      <td className="py-3 px-2 text-right font-mono text-slate-300">{s.total_solves}</td>
                      <td className="py-3 px-2 text-right font-mono hidden sm:table-cell">
                        {s.pb_single != null ? formatTime(s.pb_single) : '—'}
                      </td>
                      <td className="py-3 px-2 text-right font-mono hidden lg:table-cell">
                        {s.main_avg != null ? formatTime(s.main_avg) : '—'}
                      </td>
                      <td className="py-3 px-2 text-right">
                        {s.improvement !== null ? (
                          <span className={`font-mono text-xs ${s.improvement >= 0 ? 'text-good' : 'text-danger'}`}>
                            {s.improvement >= 0 ? '+' : ''}{s.improvement}%
                          </span>
                        ) : (
                          <span className="text-slate-600 text-xs">—</span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-center">
                        {(() => { const st = accountStatus(s); return <span className={`px-2 py-0.5 rounded-full text-[11px] ${st.cls}`}>{st.label}</span>; })()}
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className={`inline-block w-2 h-2 rounded-full ${s.today_checkin ? 'bg-good' : 'bg-slate-700'}`} title={s.today_checkin ? '今日已打卡' : '今日未打卡'} />
                      </td>
                      <td className="py-3 pr-5 pl-2 text-right text-xs text-slate-500">
                        {s.last_active_at ? timeAgo(s.last_active_at) : '从未'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 断签提醒 */}
          {needNudge > 0 && (
            <div className="card p-5 border-warn/30">
              <h2 className="font-semibold text-warn mb-3 flex items-center gap-2">
                <IconWarn />
                今日断签提醒（{needNudge} 人）
              </h2>
              <div className="flex flex-wrap gap-2">
                {students.filter((s) => !s.today_checkin).slice(0, 8).map((s) => (
                  <Link
                    key={s.id}
                    to={`/coach/students/${s.id}`}
                    className="px-3 py-1.5 rounded-lg bg-slate-700/30 text-sm text-slate-300 hover:bg-slate-700/60 transition-colors"
                  >
                    {s.nickname}
                    {s.last_active_at ? <span className="text-slate-500 ml-1.5 text-xs">{timeAgo(s.last_active_at)}</span> : null}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* 跨学生对比 */}
          <div className="card p-5">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <h2 className="font-semibold text-slate-100">跨学生成长对比</h2>
              <div className="flex items-center gap-2">
                <select className="input !w-28 !py-1.5 !text-xs" value={compareEvent} onChange={(e) => setCompareEvent(e.target.value)}>
                  {EVENTS.map((e) => (
                    <option key={e.key} value={e.key}>{e.name}</option>
                  ))}
                </select>
                <select className="input !w-28 !py-1.5 !text-xs" value={compareRange} onChange={(e) => setCompareRange(e.target.value)}>
                  <option value="7d">近7天</option>
                  <option value="30d">近30天</option>
                  <option value="90d">近3个月</option>
                  <option value="all">全部</option>
                </select>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              {students.map((s) => (
                <button
                  key={s.id}
                  onClick={() => toggleSelect(s.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${
                    selected.includes(s.id)
                      ? 'bg-accent/15 border-accent/50 text-accent'
                      : 'bg-slate-700/20 border-slate-700 text-slate-400 hover:text-slate-200'
                  }`}
                >
                  {s.nickname}
                </button>
              ))}
            </div>

            <ComparisonChart
              studentIds={selected}
              event={compareEvent}
              range={compareRange}
              studentNames={Object.fromEntries(students.map((s) => [s.id, s.nickname]))}
            />
          </div>
        </>
      )}
    </div>
  );
}

function Metric({ label, value, suffix = '', accent = false, warn = false, danger = false }) {
  const color = accent ? 'text-accent' : warn ? 'text-warn' : danger ? 'text-danger' : 'text-slate-100';
  return (
    <div className="card px-4 py-3.5">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`mt-1 font-mono text-xl font-semibold ${color}`}>{value}{suffix}</div>
    </div>
  );
}
